import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { startLoading, stopLoading } from 'app/store/utilSlice';
import ClientTaskService from 'app/shared-components/client-tasks/services/ClientTaskService';

export const fetchTasks = createAsyncThunk(
  'clientTasks/fetchTasks',
  async (_, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await ClientTaskService.getAllTasks();
      return response;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchTasksByClient = createAsyncThunk(
  'clientTasks/fetchTasksByClient',
  async (clientId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await ClientTaskService.getTasksByClient(clientId);
      return response;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const createTask = createAsyncThunk(
  'clientTasks/createTask',
  async (taskData, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await ClientTaskService.createTask(taskData);
      return response;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateTask = createAsyncThunk(
  'clientTasks/updateTask',
  async ({ taskId, taskData }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await ClientTaskService.updateTask(taskId, taskData);
      return response;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const deleteTask = createAsyncThunk(
  'clientTasks/deleteTask',
  async (taskId, { dispatch }) => {
    dispatch(startLoading());
    try {
      await ClientTaskService.deleteTask(taskId);
      return taskId;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const generateTaskCode = createAsyncThunk(
  'clientTasks/generateTaskCode',
  async (taskId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await ClientTaskService.generateTaskCode(taskId);
      return { taskId, ...response };
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchTaskAccessLog = createAsyncThunk(
  'clientTasks/fetchTaskAccessLog',
  async (taskId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await ClientTaskService.getTaskAccessLog(taskId);
      return response;
    } finally {
      dispatch(stopLoading());
    }
  }
);

const clientTasksSlice = createSlice({
  name: 'clientTasks',
  initialState: {
    tasks: [],
    taskCodes: {},
    accessLogs: {},
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch tasks by client
      .addCase(fetchTasksByClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasksByClient.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasksByClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Create task
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.push(action.payload);
      })
      // Update task
      .addCase(updateTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      // Delete task
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter(task => task.id !== action.payload);
      })
      // Generate task code
      .addCase(generateTaskCode.fulfilled, (state, action) => {
        const { taskId, code } = action.payload;
        state.taskCodes[taskId] = code;
      })
      // Fetch task access log
      .addCase(fetchTaskAccessLog.fulfilled, (state, action) => {
        // Access logs are returned directly, no need to store in state
        // as they're used immediately in the component
      });
  }
});

export const { clearError } = clientTasksSlice.actions;

export const selectAllTasks = () => (state) => state.clientTasks?.tasks || [];
export const selectTasksLoading = () => (state) => state.clientTasks?.loading || false;
export const selectTasksError = () => (state) => state.clientTasks?.error;
export const selectTaskAccessLogs = (taskId) => (state) => state.clientTasks?.accessLogs?.[taskId] || [];

export { clientTasksSlice };
export default clientTasksSlice.reducer;